import React, { useState, useEffect } from 'react';
import {
  BuildingOffice2Icon,
  UserPlusIcon,
  XMarkIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabaseClient';
import { useUser } from '../../context/UserContext';

export default function NoCompanyState() {
  const [drawer, setDrawer] = useState(null); // 'join' | 'create' | null
  const [inviteCode, setInviteCode] = useState('');
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { refreshUserData } = useUser();

  // Close drawer on escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    if (drawer) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawer]);

  const closeDrawer = () => {
    setDrawer(null);
    setError(null);
    setInviteCode('');
    setTeamName('');
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { error: functionError } = await supabase.functions.invoke('add-to-company', {
        body: { invite_code: inviteCode.trim() },
      });

      if (functionError) throw new Error(functionError.message);

      await refreshUserData();
      closeDrawer();
    } catch (err) {
      try {
        const parsed = JSON.parse(err.message);
        setError(parsed.error || 'Failed to join team.');
      } catch {
        setError(err.message || 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { error: functionError } = await supabase.functions.invoke('create-company', {
        body: { teamName: teamName.trim() },
      });

      if (functionError) throw new Error(functionError.message);

      await refreshUserData();
      closeDrawer();
    } catch (err) {
      try {
        const parsed = JSON.parse(err.message);
        setError(parsed.error || 'Failed to create team.');
      } catch {
        setError(err.message || 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main content */}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-gray-900">Get started with a team</h2>
          <p className="mt-1 text-sm text-gray-500">
            Join an existing team or create a new one to start collaborating.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Join card */}
          <div className="border border-gray-200 rounded-lg bg-white p-6 flex flex-col">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
              <UserPlusIcon className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Join an existing team</h3>
            <p className="mt-1 text-sm text-gray-500 flex-1">
              Enter an invite code from your team administrator to get connected.
            </p>
            <button
              onClick={() => setDrawer('join')}
              className="mt-4 w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Enter Invite Code
            </button>
          </div>

          {/* Create card */}
          <div className="border border-gray-200 rounded-lg bg-white p-6 flex flex-col">
            <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center mb-4">
              <BuildingOffice2Icon className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Create a new team</h3>
            <p className="mt-1 text-sm text-gray-500 flex-1">
              Start a new workspace with a 14-day free trial. No credit card required.
            </p>
            <button
              onClick={() => setDrawer('create')}
              className="mt-4 w-full px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
            >
              Create Team
            </button>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {drawer && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={closeDrawer}
        />
      )}

      {/* Side drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-200 ease-out ${
          drawer ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Drawer header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {drawer === 'join' ? 'Join a Team' : 'Create a Team'}
            </h3>
            <button
              onClick={closeDrawer}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Drawer body */}
          <div className="flex-1 px-6 py-6">
            {drawer === 'join' && (
              <form onSubmit={handleJoinSubmit} className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter the invite code provided by your team administrator.
                  </p>
                  <label htmlFor="invite-code" className="block text-sm font-medium text-gray-700 mb-1">
                    Invite Code
                  </label>
                  <input
                    type="text"
                    id="invite-code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="e.g. AB12CD34"
                    className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={loading}
                    autoFocus
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !inviteCode.trim()}
                  className="w-full px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Joining...' : 'Join Team'}
                </button>
              </form>
            )}

            {drawer === 'create' && (
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Create a new team workspace. You'll get a 14-day free trial — no credit card required.
                  </p>
                  <label htmlFor="team-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name
                  </label>
                  <input
                    type="text"
                    id="team-name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="e.g. Acme Paving"
                    className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={loading}
                    autoFocus
                    required
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !teamName.trim()}
                  className="w-full px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Team & Start Trial'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
